import { Box, Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import "./creativeModal.css";
import { CreativeType, FiltersType } from "../../types/types";
import { FileField, InputField } from "../atoms/inputField";
import { PptField } from "../atoms/pptField";
import { ImageGalleryField } from "../atoms/imageGalleryField";
import { VideoField } from "../atoms/videoField";
import { useFormik } from "formik";
import snackbar from "../../api/snackbar/useSnackbar";
import {
  useCreateCreativeMutation,
  useUpdateCreativeMutation,
} from "../../api/creative";
import { CreativeSchema } from "../../validationSchema/creativeSchema";
import { SelectBox, SelectBoxV1 } from "../atoms/selectBox";
import { ButtonV1 } from "../atoms/button";
import { useGetAdTypeQuery } from "../../api/filters/adType";
import { useGetBrandQuery } from "../../api/filters/brand";
import { useGetCategoryQuery } from "../../api/filters/category";
import { useGetCountryQuery } from "../../api/filters/country";
import { useGetLanguageQuery } from "../../api/filters/language";
import { useGetOrientationQuery } from "../../api/filters/orientation";
import { useGetRegionQuery } from "../../api/filters/region";
import { useGetSubBrandQuery } from "../../api/filters/subBrand";
import { useGetFeatureQuery } from "../../api/filters/feature";
import { useGetKeywordQuery } from "../../api/filters/keyword";
import { useGetSpecialKeywordQuery } from "../../api/filters/specialKeyword";
import { useEffect } from "react";
import { useGetCreativeFormatQuery } from "../../api/filters/creativeFormat";
import { useGetYearQuery } from "../../api/filters/year";
import { useGetStageQuery } from "../../api/filters/stage";
import { useGetBrandCollabQuery } from "../../api/filters/brandCollab";
import { useGetDtLevelQuery } from "../../api/filters/dtLevels";

interface propsV2 {
  item?: CreativeType;
  open: boolean;
  handleClose: Function;
  setPpt: Function;
  setPptUrl: Function;
}

interface ItemSchema {
  id: string;
  title: string;
}

export function AddCreativeModal({ open, handleClose, setPpt, setPptUrl }: propsV2) {
  const [create, { isLoading: createIsLoading, isSuccess: createSuccess }] =
    useCreateCreativeMutation({});

  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "70vw",
    height: "90vh",
    borderRadius: "1.5rem",
    bgcolor: "background.paper",
    border: "none",
    boxShadow: 4,
  };

  const Formik = useFormik({
    initialValues: {
      ID: "",
      adTypes: [],
      brandId: undefined,
      brandCollab: [],
      categories: [],
      countries: [],
      creativeFormats: [],
      features: [],
      gDriveLink: undefined,
      isPublished: false,
      keywords: [],
      lang: undefined,
      newTitle: undefined,
      orientation: undefined,
      regionId: undefined,
      subBrands: [],
      specialKeywords: [],
      title: undefined,
      year: undefined,
      dt: undefined,
      fileName: undefined,
      vimeoLink: undefined,
      stage: undefined,
      videoLink: undefined,
      thumbImage: undefined,
      thumbs: [],
      pptDetails: {
        pptLink: undefined,
        pdfLink: undefined,
      },
      pptLink: undefined,
      imageGallery: [],
      mediaType: "video",
    },
    enableReinitialize: false,
    validationSchema: CreativeSchema,
    onSubmit: async (values) => {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: `Engage_Tap_Add_Creative`,
          category: "User Interaction",
          action: "Button Click",
          label: `Add Creative Button`,
        });
      }
      if (values.mediaType === "ppt") {
        if (!values.pptDetails?.pptLink || !values.pptDetails?.pdfLink) {
          snackbar.error("Please upload a PowerPoint document");
          return;
        }
      } else if (values.mediaType === "video") {
        if (!values.videoLink || !values.thumbImage || values.thumbs.length === 0) {
          snackbar.error("Please upload a video and thumbnail image");
          return;
        }
      } else if (values.mediaType === "image") {
        if (!values.imageGallery || values.imageGallery.length === 0) {
          snackbar.error("Please upload an image gallery");
          return;
        }
      }
      await create(values)
        .unwrap()
        .then((res) => {
          console.log(res);
          snackbar.success(res.message);
          handleClose();
        })
        .catch((error: { data: { message: string } }) => {
          console.log(error);
          snackbar.error(error?.data?.message);
        });
    },
  });

  const { data: AdTypeData } = useGetAdTypeQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { data: BrandData } = useGetBrandQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { data: BrandCollabData } = useGetBrandCollabQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { data: CategoryData } = useGetCategoryQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { data: KeywordData } = useGetKeywordQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 10000,  // Increased to handle large keyword lists
    pageNo: 1,
  }, {
    refetchOnMountOrArgChange: true  // Always fetch fresh data
  });
  const { data: LanguageData } = useGetLanguageQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { data: OrientationData } = useGetOrientationQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { data: RegionData } = useGetRegionQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { data: SpecialKeywords } = useGetSpecialKeywordQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { data: FeatureData } = useGetFeatureQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { data: CreativeFormatData } = useGetCreativeFormatQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { data: DtLevelData } = useGetDtLevelQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { data: YearData } = useGetYearQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { data: StageData } = useGetStageQuery({
    scope: "admin",
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });
  const { refetch: subBrandsRefetch, data: SubBrandData } = useGetSubBrandQuery(
    {
      scope: "admin",
      brand: Formik.values.brandId,
      sortType: "title",
      sortOrder: 1,
      pageSize: 5000,
      pageNo: 1,
    }
  );
  const { refetch: countryRefetch, data: CountryData } = useGetCountryQuery({
    scope: "admin",
    region: Formik.values.regionId,
    sortType: "title",
    sortOrder: 1,
    pageSize: 5000,
    pageNo: 1,
  });

  useEffect(() => {
    subBrandsRefetch();
    countryRefetch();
  }, [Formik.values.brandId, Formik.values.regionId]);


  return (
    <Modal
      open={open}
      onClose={() => handleClose()}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style} height={"90vh"} overflow={"scroll"}>
        <div
          className="absolute right-4 top-4 cursor-pointer"
          onClick={() => handleClose()}
          role="presentation"
        >
          <Close className="" />
        </div>
        <h1 className="font-semibold text-xl py-6 pb-2 px-8 mb-4 border-b">
          Creative New
        </h1>
        <form
          className="w-full flex flex-wrap p-8 pb-0 pt-0 justify-between"
          noValidate
          onSubmit={Formik.handleSubmit}
        >
          <InputField
            width="half"
            name="title"
            label="title"
            type="text"
            placeholder="Title"
            formik={Formik}
          />
          <InputField
            width="half"
            name="newTitle"
            label="new title"
            type="text"
            placeholder="New title"
            formik={Formik}
          />
          <div className="w-full flex gap-4 mb-4">
            {/* Media Type Selector */}
            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Media Type
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="mediaType"
                    value="video"
                    checked={Formik.values.mediaType === "video"}
                    onChange={Formik.handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Video</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="mediaType"
                    value="ppt"
                    checked={Formik.values.mediaType === "ppt"}
                    onChange={Formik.handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">PowerPoint</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="mediaType"
                    value="image"
                    checked={Formik.values.mediaType === "image"}
                    onChange={Formik.handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Image Gallery</span>
                </label>
              </div>
              {Formik.touched.mediaType && Formik.errors.mediaType && (
                <div className="text-red-600 text-sm mt-1">{Formik.errors.mediaType}</div>
              )}
            </div>

            {/* Creative ID Field */}
            <div className="w-1/2" style={{ marginLeft: '40px' }}>
              <InputField
                formik={Formik}
                label="creative id"
                name="ID"
                type="text"
                placeholder="Enter Creative ID"
              />
            </div>
          </div>

          {/* Media Type Specific Validation Error */}
          {Formik.errors.mediaType && typeof Formik.errors.mediaType === 'string' && Formik.errors.mediaType.includes('Please fill required fields') && (
            <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="text-red-800 text-sm font-medium mb-1">Media Type Requirements:</div>
              <div className="text-red-700 text-sm">
                {Formik.errors.mediaType}
              </div>
            </div>
          )}

          {/* Conditional Media Fields */}
          {Formik.values.mediaType === "video" && (
            <>
              <VideoField
                name="video"
                label="Video"
                formik={Formik}
              />
              {/* Video Field Validation Errors */}
              <div className="w-full">
                {Formik.touched.fileName && Formik.errors.fileName && (
                  <div className="text-red-600 text-sm mt-1">File Name: {Formik.errors.fileName}</div>
                )}
                {Formik.touched.videoLink && Formik.errors.videoLink && (
                  <div className="text-red-600 text-sm mt-1">Video Link: {Formik.errors.videoLink}</div>
                )}
                {Formik.touched.thumbImage && Formik.errors.thumbImage && (
                  <div className="text-red-600 text-sm mt-1">Thumbnail Image: {Formik.errors.thumbImage}</div>
                )}
                {Formik.touched.thumbs && Formik.errors.thumbs && (
                  <div className="text-red-600 text-sm mt-1">Thumbnails: {Formik.errors.thumbs}</div>
                )}
              </div>
            </>
          )}

          {Formik.values.mediaType === "ppt" && (
            <>
              <PptField
                name="pptDetails"
                label="PowerPoint Document"
                formik={Formik}
                setPpt={setPpt}
                setPptUrl={setPptUrl}
              />
              {/* PPT Field Validation Errors */}
              <div className="w-full">
                {Formik.touched.pptDetails?.pptLink && Formik.errors.pptDetails?.pptLink && (
                  <div className="text-red-600 text-sm mt-1">PPT Link: {Formik.errors.pptDetails.pptLink}</div>
                )}
                {Formik.touched.pptDetails?.pdfLink && Formik.errors.pptDetails?.pdfLink && (
                  <div className="text-red-600 text-sm mt-1">PPT Link: {Formik.errors.pptDetails.pdfLink}</div>
                )}
              </div>
            </>
          )}

          {Formik.values.mediaType === "image" && (
            <>
              <ImageGalleryField
                name="imageGallery"
                label="Image Gallery"
                formik={Formik}
              />
              {/* Image Gallery Field Validation Errors */}
              <div className="w-full">
                {Formik.touched.imageGallery && Formik.errors.imageGallery && (
                  <div className="text-red-600 text-sm mt-1">Image Gallery: {Formik.errors.imageGallery}</div>
                )}
              </div>
            </>
          )}
          <SelectBox
            formik={Formik}
            label="AdType"
            name="adTypes"
            options={AdTypeData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBox
            formik={Formik}
            label="Category"
            name="categories"
            options={CategoryData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBoxV1
            formik={Formik}
            label="Brands"
            name="brandId"
            options={BrandData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBox
            formik={Formik}
            label="Sub Brands"
            name="subBrands"
            options={SubBrandData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBox
            formik={Formik}
            label="Brand Collab"
            name="brandCollab"
            options={BrandCollabData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBoxV1
            formik={Formik}
            label="region"
            name="regionId"
            options={RegionData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBox
            formik={Formik}
            label="Country"
            name="countries"
            options={CountryData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBox
            formik={Formik}
            label="Feature"
            name="features"
            options={FeatureData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBoxV1
            formik={Formik}
            label="orientation"
            name="orientation"
            options={OrientationData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBoxV1
            formik={Formik}
            label="Stage"
            name="stage"
            options={StageData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBox
            formik={Formik}
            label="Keyword"
            name="keywords"
            options={KeywordData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBoxV1
            formik={Formik}
            label="Language"
            name="lang"
            options={LanguageData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBoxV1
            formik={Formik}
            label="Year"
            name="year"
            options={YearData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBoxV1
            formik={Formik}
            label="Dt Levels"
            name="dt"
            options={DtLevelData?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <SelectBox
            formik={Formik}
            label="CreativeFormats"
            name="creativeFormats"
            options={CreativeFormatData?.data?.data.map(
              (item: FiltersType) => ({
                id: item._id,
                title: item.title,
              })
            )}
          />
          <InputField
            width="half"
            name="gDriveLink"
            label="G Drive Link"
            type="text"
            placeholder="G Drive Link"
            formik={Formik}
          />
          {
            Formik.values.mediaType === "video" && (
              <InputField
                width="half"
                name="vimeoLink"
                label="vimeo Link"
                type="text"
                placeholder="Vimeo Link"
                formik={Formik}
              />
            )
          }
          <SelectBox
            formik={Formik}
            label="Special Keyword"
            name="specialKeywords"
            options={SpecialKeywords?.data?.data.map((item: FiltersType) => ({
              id: item._id,
              title: item.title,
            }))}
          />
          <InputField
            width="half"
            formik={Formik}
            label="File Name"
            name="fileName"
            type="text"
            placeholder="File Name"
          />
          <div className="w-full flex justify-end p-8 pt-0">
            <div className="w-max">
              <ButtonV1 color="blue" type="submit">
                Save
              </ButtonV1>
            </div>
          </div>
        </form>
      </Box>
    </Modal>
  );
}

export function EditCreativeModal({ item, open, handleClose, setPpt, setPptUrl }: propsV2) {
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "70vw",
    height: "90vh",
    bgcolor: "background.paper",
    border: "none",
    borderRadius: "1.5rem",
    boxShadow: 4,
  };

  const [update, { isLoading: updateIsLoading, isSuccess: updateSuccess }] =
    useUpdateCreativeMutation({});

  const Formik = useFormik({
    initialValues: {
      ID: item?.ID || "",
      adTypes: item?.adTypes?.map((item) => item) || [],
      brandId: item?.brandId,
      brandCollab: item?.brandCollab || [],
      categories: item?.categories?.map((item) => item) || [],
      countries: item?.countries?.map((item) => item) || [],
      creativeFormats: item?.creativeFormats?.map((item) => item) || [],
      features: item?.features?.map((item) => item) || [],
      gDriveLink: item?.gDriveLink,
      isPublished: item?.isPublished,
      keywords: item?.keywords?.map((item) => item) || [],
      lang: item?.lang,
      newTitle: item?.newTitle,
      orientation: item?.orientation,
      regionId: item?.regionId,
      subBrands: item?.subBrands.map((item) => item) || [],
      specialKeywords: item?.specialKeywords?.map((item) => item) || [],
      title: item?.title,
      year: item?.year,
      dt: item?.dt,
      vimeoLink: item?.vimeoLink,
      fileName: item?.fileName,
      stage: item?.stage,
      videoLink: item?.videoLink || undefined,
      thumbImage: item?.thumbImage || undefined,
      thumbs: item?.thumbs || [undefined],
      pptDetails: {
        pptLink: item?.pptDetails?.pptLink || undefined,
        pdfLink: item?.pptDetails?.pdfLink || undefined,
      },
      imageGallery: item?.imageGallery || [],
      mediaType: item?.mediaType || undefined,
    },
    enableReinitialize: true,
    validationSchema: CreativeSchema,
    onSubmit: (values) => {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: `Engage_Tap_Edit_Creative`,
          category: "User Interaction",
          action: "Button Click",
          label: `Edit Creative Button`,
        });
      }
      if (values.mediaType === "ppt") {
        if (!values.pptDetails?.pptLink || !values.pptDetails?.pdfLink) {
          snackbar.error("Please upload a PowerPoint document");
          return;
        }
      } else if (values.mediaType === "video") {
        if (!values.videoLink || !values.thumbImage || values.thumbs.length === 0) {
          snackbar.error("Please upload a video and thumbnail image");
          return;
        }
      } else if (values.mediaType === "image") {
        if (!values.imageGallery || values.imageGallery.length === 0) {
          snackbar.error("Please upload an image gallery");
          return;
        }
      }
      update({ id: item?._id, body: values })
        .unwrap()
        .then((res) => {
          console.log(res);
          snackbar.success(res.message);
          handleClose();
        })
        .catch((error: { data: { message: string } }) => {
          console.log(error);
          snackbar.error(error?.data?.message);
        });
    },
  });

  const { data: AdTypeData, isFetching: adTypesIsLoading } = useGetAdTypeQuery(
    {
      scope: "admin",
      pageSize: 5000,
      pageNo: 1,
    },
    { skip: !open }
  );
  const { data: BrandData, isFetching: brandIsLoading } = useGetBrandQuery(
    {
      scope: "admin",
      sortType: "title",
      sortOrder: 1,
      pageSize: 5000,
      pageNo: 1,
    },
    { skip: !open }
  );
  const { data: BrandCollabData, isFetching: brandCollabIsLoading } =
    useGetBrandCollabQuery(
      {
        scope: "admin",
        sortType: "title",
        sortOrder: 1,
        pageSize: 5000,
        pageNo: 1,
      },
      { skip: !open }
    );
  const { data: CategoryData, isFetching: categoryIsLoading } =
    useGetCategoryQuery(
      {
        scope: "admin",
        sortType: "title",
        sortOrder: 1,
        pageSize: 5000,
        pageNo: 1,
      },
      { skip: !open }
    );
  const { data: KeywordData, isFetching: keyWordIsLoading } =
    useGetKeywordQuery(
      {
        scope: "admin",
        sortType: "title",
        sortOrder: 1,
        pageSize: 10000,  // Increased to handle large keyword lists
        pageNo: 1,
      },
      {
        skip: !open,
        refetchOnMountOrArgChange: true  // Always fetch fresh data when modal opens
      }
    );
  const { data: LanguageData, isFetching: languageIsLoading } =
    useGetLanguageQuery(
      {
        scope: "admin",
        sortType: "title",
        sortOrder: 1,
        pageSize: 5000,
        pageNo: 1,
      },
      { skip: !open }
    );
  const { data: OrientationData, isFetching: orientationIsLoading } =
    useGetOrientationQuery(
      {
        scope: "admin",
        sortType: "title",
        sortOrder: 1,
        pageSize: 5000,
        pageNo: 1,
      },
      { skip: !open }
    );
  const { data: RegionData, isFetching: regionIsLoading } = useGetRegionQuery(
    {
      scope: "admin",
      sortType: "title",
      sortOrder: 1,
      pageSize: 5000,
      pageNo: 1,
    },
    { skip: !open }
  );
  const { data: FeatureData, isFetching: featureIsLoading } =
    useGetFeatureQuery(
      {
        scope: "admin",
        sortType: "title",
        sortOrder: 1,
        pageSize: 5000,
        pageNo: 1,
      },
      { skip: !open }
    );
  const { data: SpecialKeywords, isFetching: specialKeywordIsLoading } =
    useGetSpecialKeywordQuery(
      {
        scope: "admin",
        sortType: "title",
        sortOrder: 1,
        pageSize: 5000,
        pageNo: 1,
      },
      { skip: !open }
    );
  const { data: CreativeFormatData, isFetching: creativeFormatIsLoading } =
    useGetCreativeFormatQuery(
      {
        scope: "admin",
        sortType: "title",
        sortOrder: 1,
        pageSize: 5000,
        pageNo: 1,
      },
      { skip: !open }
    );
  const { data: DtLevelData } = useGetDtLevelQuery(
    {
      scope: "admin",
      sortType: "title",
      sortOrder: 1,
      pageSize: 5000,
      pageNo: 1,
    },
    { skip: !open }
  );
  const { data: StageData, isFetching: stageIsLoading } = useGetStageQuery(
    {
      scope: "admin",
      sortType: "title",
      sortOrder: 1,
      pageSize: 5000,
      pageNo: 1,
    },
    { skip: !open }
  );
  const { data: YearData, isFetching: yearIsLoading } = useGetYearQuery(
    {
      scope: "admin",
      sortType: "title",
      sortOrder: 1,
      pageSize: 5000,
      pageNo: 1,
    },
    { skip: !open }
  );
  const {
    refetch: subBrandsRefetch,
    data: SubBrandData,
    isFetching: subBrandIsLoading,
  } = useGetSubBrandQuery(
    {
      scope: "admin",
      brand: Formik.values.brandId,
      sortType: "title",
      sortOrder: 1,
      pageSize: 5000,
      pageNo: 1,
    },
    { skip: !open || !Formik.values.brandId }
  );
  const {
    refetch: countryRefetch,
    data: CountryData,
    isFetching: countryIsLoading,
  } = useGetCountryQuery(
    {
      scope: "admin",
      region: Formik.values.regionId,
      sortType: "title",
      sortOrder: 1,
      pageSize: 5000,
      pageNo: 1,
    },
    { skip: !open || !Formik.values.regionId }
  );

  useEffect(() => {
    if (open) {
      if (Formik.values.brandId) {
        subBrandsRefetch();
      }
      if (Formik.values.regionId) {
        countryRefetch();
      }
    }
  }, [Formik.values.brandId, Formik.values.regionId, open]);

  return (
    <Modal
      open={open}
      onClose={() => handleClose()}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style} height={"90vh"} overflow={"scroll"}>
        <div
          className="absolute right-4 top-4 cursor-pointer"
          onClick={() => handleClose()}
          role="presentation"
        >
          <Close className="" />
        </div>
        <h1 className="font-semibold text-xl py-6 pb-2 px-8 mb-4 border-b">
          Update Creative
        </h1>
        {adTypesIsLoading ||
          brandIsLoading ||
          brandCollabIsLoading ||
          categoryIsLoading ||
          countryIsLoading ||
          keyWordIsLoading ||
          languageIsLoading ||
          orientationIsLoading ||
          regionIsLoading ||
          featureIsLoading ||
          specialKeywordIsLoading ||
          stageIsLoading ||
          creativeFormatIsLoading ||
          yearIsLoading ||
          subBrandIsLoading ? (
          <p className="w-full h-full text-2xl text-center">Loading..</p>
        ) : (
          <form
            className="w-full flex flex-wrap p-8 pb-0 pt-0 justify-between"
            noValidate
            onSubmit={Formik.handleSubmit}
          >
            <InputField
              width="half"
              name="title"
              label="title"
              type="text"
              placeholder="Title"
              formik={Formik}
            />
            <InputField
              width="half"
              name="newTitle"
              label="new title"
              type="text"
              placeholder="New title"
              formik={Formik}
            />
            <div className="w-full flex gap-4 mb-4">
              {/* Media Type Selector */}
              <div className="w-1/2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Media Type
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="mediaType"
                      value="video"
                      checked={Formik.values.mediaType === "video"}
                      onChange={Formik.handleChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Video</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="mediaType"
                      value="ppt"
                      checked={Formik.values.mediaType === "ppt"}
                      onChange={Formik.handleChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">PowerPoint</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="mediaType"
                      value="image"
                      checked={Formik.values.mediaType === "image"}
                      onChange={Formik.handleChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Image Gallery</span>
                  </label>
                </div>
                {/* Media Type Validation Error */}
                {Formik.touched.mediaType && Formik.errors.mediaType && (
                  <div className="text-red-600 text-sm mt-1">{Formik.errors.mediaType}</div>
                )}
              </div>

              {/* Creative ID Field */}
              <div className="w-1/2" style={{ marginLeft: '40px' }}>
                <InputField
                  formik={Formik}
                  label="Creative ID"
                  name="ID"
                  type="text"
                  placeholder="Enter Creative ID"
                />
              </div>
            </div>

            {/* Media Type Specific Validation Error */}
            {Formik.errors.mediaType && typeof Formik.errors.mediaType === 'string' && Formik.errors.mediaType.includes('Please fill required fields') && (
              <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="text-red-800 text-sm font-medium mb-1">Media Type Requirements:</div>
                <div className="text-red-700 text-sm">
                  {Formik.errors.mediaType}
                </div>
              </div>
            )}

            {/* Conditional Media Fields */}
            {Formik.values.mediaType === "video" && (
              <>
                <VideoField
                  name="video"
                  label="Video"
                  formik={Formik}
                />
                {/* Video Field Validation Errors */}
                <div className="w-full">
                  {Formik.touched.fileName && Formik.errors.fileName && (
                    <div className="text-red-600 text-sm mt-1">File Name: {Formik.errors.fileName}</div>
                  )}
                  {Formik.touched.videoLink && Formik.errors.videoLink && (
                    <div className="text-red-600 text-sm mt-1">Video Link: {Formik.errors.videoLink}</div>
                  )}
                  {Formik.touched.thumbImage && Formik.errors.thumbImage && (
                    <div className="text-red-600 text-sm mt-1">Thumbnail Image: {Formik.errors.thumbImage}</div>
                  )}
                  {Formik.touched.thumbs && Formik.errors.thumbs && (
                    <div className="text-red-600 text-sm mt-1">Thumbnails: {Formik.errors.thumbs}</div>
                  )}
                </div>
              </>
            )}

            {Formik.values.mediaType === "ppt" && (
              <>
                <PptField
                  name="pptDetails"
                  label="PowerPoint Document"
                  formik={Formik}
                  setPpt={setPpt}
                  setPptUrl={setPptUrl}
                />
                {/* PPT Field Validation Errors */}
                <div className="w-full">
                  {Formik.touched.pptDetails?.pptLink && Formik.errors.pptDetails?.pptLink && (
                    <div className="text-red-600 text-sm mt-1">PPT Link: {Formik.errors.pptDetails.pptLink}</div>
                  )}
                  {Formik.touched.pptDetails?.pdfLink && Formik.errors.pptDetails?.pdfLink && (
                    <div className="text-red-600 text-sm mt-1">PPT Link: {Formik.errors.pptDetails.pdfLink}</div>
                  )}
                </div>
              </>
            )}

            {Formik.values.mediaType === "image" && (
              <>
                <ImageGalleryField
                  name="imageGallery"
                  label="Image Gallery"
                  formik={Formik}
                />
                {/* Image Gallery Field Validation Errors */}
                <div className="w-full">
                  {Formik.touched.imageGallery && Formik.errors.imageGallery && (
                    <div className="text-red-600 text-sm mt-1">Image Gallery: {Formik.errors.imageGallery}</div>
                  )}
                </div>
              </>
            )}
            <SelectBox
              formik={Formik}
              label="AdType"
              name="adTypes"
              options={AdTypeData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBox
              formik={Formik}
              label="Category"
              name="categories"
              options={CategoryData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBoxV1
              formik={Formik}
              label="Brands"
              name="brandId"
              options={BrandData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBox
              formik={Formik}
              label="Sub Brands"
              name="subBrands"
              options={SubBrandData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBox
              formik={Formik}
              label="Brand Collab"
              name="brandCollab"
              options={BrandCollabData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBoxV1
              formik={Formik}
              label="region"
              name="regionId"
              options={RegionData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBox
              formik={Formik}
              label="Country"
              name="countries"
              options={CountryData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBox
              formik={Formik}
              label="Feature"
              name="features"
              options={FeatureData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBoxV1
              formik={Formik}
              label="orientation"
              name="orientation"
              options={OrientationData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBoxV1
              formik={Formik}
              label="Stage"
              name="stage"
              options={StageData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBox
              formik={Formik}
              label="Keyword"
              name="keywords"
              options={KeywordData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBoxV1
              formik={Formik}
              label="Language"
              name="lang"
              options={LanguageData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBoxV1
              formik={Formik}
              label="Year"
              name="year"
              options={YearData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBoxV1
              formik={Formik}
              label="Dt Levels"
              name="dt"
              options={DtLevelData?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <SelectBox
              formik={Formik}
              label="CreativeFormats"
              name="creativeFormats"
              options={CreativeFormatData?.data?.data.map(
                (item: FiltersType) => ({
                  id: item._id,
                  title: item.title,
                })
              )}
            />
            <InputField
              width="half"
              name="gDriveLink"
              label="G Drive Link"
              type="text"
              placeholder="G Drive Link"
              formik={Formik}
            />
            {
              Formik.values.mediaType === "video" ?
                <InputField
                  width="half"
                  name="vimeoLink"
                  label="vimeo Link"
                  type="text"
                  placeholder="Vimeo Link"
                  formik={Formik}
                /> : <></>
            }
            <SelectBox
              formik={Formik}
              label="Special Keyword"
              name="specialKeywords"
              options={SpecialKeywords?.data?.data.map((item: FiltersType) => ({
                id: item._id,
                title: item.title,
              }))}
            />
            <InputField
              width="half"
              formik={Formik}
              label="File Name"
              name="fileName"
              type="text"
              placeholder="File Name"
            />
            <div className="w-full flex justify-end p-8 pt-0">
              <div className="w-max">
                <ButtonV1 color="blue" type="submit">
                  Save
                </ButtonV1>
              </div>
            </div>
          </form>
        )}
      </Box>
    </Modal>
  );
}
